import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { AQDB_SERVER_URL } from '../../config';
import { AqdbBatch, FieldOfQualityDataBatch } from '../models/aqdb-batch';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { AqdbConditions, AqdbMeMoFlag } from '../models/aqdb-constants';
import { OrderBy } from '../../shared/utils/constant';

const GET_AQDB_SERVER_URL = `${ AQDB_SERVER_URL }/v3/qualityData`;

export const QUALITY_DATA_BATCH_FIELD: FieldOfQualityDataBatch[] =
	[
		'id',
		'batchReference',
		'deliveryMillDescription',
		'nominalThickness',
		'qtyBatchGross',
		'qtyBatchNet',
		'despatchAdviceNumber',
		'serialNumber',
		'recipientLabel',
		'shipmentDate',
		'articleReference',
		'commercialQualityLabel',
		'form',
		'srf',
		'receivingMillCode',
		'metalUnitMill',
		'externalDiameter',
		'metalUnitDate',
		'castingMill',
		'castingDate',
		'numberOfSegments',
		'segments.number',
		'segments.ysAverage',
		'segments.utsAverage',
		'segments.epercentAverage',
		'segments.bh2Min',
		'segments.markedIdentity',
		'segments.tensileList.testNumber',
		'segments.tensileList.sampleDirectionCode',
		'segments.tensileList.valueYs',
		'segments.tensileList.unitYs',
		'segments.tensileList.ultimateTensileStrength',
		'segments.tensileList.unitUTS',
		'segments.tensileList.elongation',
		'segments.tensileList.strainHardeningRatio',
		'segments.anisotropyList.anisotropyCoefficient',
		'segments.chemicalList.contentC',
		'segments.chemicalList.contentMn',
		'segments.chemicalList.contentP',
		'segments.chemicalList.contentS',
		'segments.chemicalList.contentSi',
		'segments.chemicalList.contentAl',
		'segments.chemicalList.contentTi',
		'segments.chemicalList.contentNb',
		'segments.chemicalList.contentV',
		'segments.chemicalList.contentCr',
		'segments.chemicalList.carbonEquivalentValue',
		'segments.chemicalList.carbonEquivalentFormula',
		'segments.magneticCharacteristicList.camaTypmth',
		'segments.magneticCharacteristicList.losses1T',
		'segments.magneticCharacteristicList.losses1T5',
		'segments.magneticCharacteristicList.induction2500w',
		'segments.magneticCharacteristicList.induction5000w',
		'segments.magneticCharacteristicList.induction10000W',
		'segments.metalCoatingList.spotNumber',
		'segments.metalCoatingList.face',
		'segments.metalCoatingList.border1Quantity',
		'segments.metalCoatingList.border2Quantity',
		'segments.metalCoatingList.averageQuantity',
		'segments.metalCoatingList.centerQuantity',
		'segments.metalCoatingList.unit',
		'segments.organicCoatingList.face',
		'segments.roughnessList.face',
		'segments.roughnessList.valueRa',
		'segments.roughnessList.valueW',
		'segments.roughnessList.cutOff',
		'segments.chemicalSurfaceTreatmentList.face'
	];

@Injectable({ providedIn: 'root' })
export class AqdbQualityDataService implements OnDestroy {
	constructor(
		private readonly http: HttpClient
	) {}

	ngOnDestroy(): void {
		throw new Error('Method not implemented.');
	}

	getQualityData(
		fields?: readonly FieldOfQualityDataBatch[],
		filter?: string,
		index?: number,
		limit?: number,
		orderBy?: OrderBy<keyof AqdbBatch>[] | OrderBy<keyof AqdbBatch>,
		distinct?: boolean,
		onlyCount?: boolean,
	) {
		const url = new URL(GET_AQDB_SERVER_URL);
		const params = url.searchParams;
		if (fields) {
			params.append('fields', fields.join(','));
		}
		// Filters
		if (filter) {
			params.append('filter', filter);
		}
		if (orderBy) {
			params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		}
		if (index !== undefined && limit !== undefined) {
			params.append('page', String(Math.floor(index / limit) + 1));
		}
		if (limit !== undefined) {
			params.append('limit', String(limit));
		}
		if (distinct) {
			params.append('distinct', String(distinct));
		}
		if (onlyCount) {
			params.append('onlyCount', String(onlyCount));
		}
		return this.http.get<PaginatedResult<AqdbBatch>>(url.toString());
	}

	isDisplayedData(isValidServiceAvailable: boolean, conditions: AqdbConditions) {
		return conditions === AqdbConditions.T || isValidServiceAvailable;
	}

	isDisplayedDataForCoatingSurface(isValidServiceAvailable: boolean, conditions: AqdbConditions) {
		// condition check only for OrganicCoating and SurfaceCleanliness
		return isValidServiceAvailable || conditions !== AqdbConditions.V;
	}

	isTransmittedOnCertificateData(isValidServiceAvailable: boolean, conditions: AqdbConditions) {
		return isValidServiceAvailable && conditions === AqdbConditions.T;
	}

	isMeasuredData(isValidServiceAvailable: boolean, conditions: AqdbConditions, meMoFlag: AqdbMeMoFlag) {
		return isValidServiceAvailable && conditions === AqdbConditions.T && meMoFlag === AqdbMeMoFlag.MEASURED_DATAS;
	}

	isModelledData(isValidServiceAvailable: boolean, conditions: AqdbConditions, meMoFlag: AqdbMeMoFlag) {
		return isValidServiceAvailable && conditions === AqdbConditions.T && meMoFlag === AqdbMeMoFlag.MODELLED_DATA;
	}

	getQualityDataBatchById(
		qualityDataBatchId: string,
		fields: readonly FieldOfQualityDataBatch[]
	) {
		const url = new URL(`${ GET_AQDB_SERVER_URL }/${ qualityDataBatchId }`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		return this.http.get<AqdbBatch>(url.toString());
	}
}
